import React from 'react'
import { Link } from 'react-router-dom'
import { ClipboardCheck, MapPin, Building2 } from 'lucide-react'
import { Badge } from 'components/ui/badge'
import { cn } from 'lib/utils'

/**
 * Status color tokens shared with `Evaluations_IndexCN.tsx` (also used by the
 * list/table view in the same file). Move-to-shared-constants candidate if
 * other surfaces need them later.
 */
export const STATUS_COLORS: Record<string, string> = {
	DRAFT: 'bg-gray-100 text-gray-700',
	IN_ANALYSIS: 'bg-blue-100 text-blue-700',
	UNDER_REVIEW: 'bg-amber-100 text-amber-700',
	APPROVED: 'bg-green-100 text-green-700',
	REJECTED: 'bg-red-100 text-red-700',
	ARCHIVED: 'bg-gray-200 text-gray-500',
}

export type EvaluationCardCNSite = {
	imageUrl?: string | null
	name: string
}

export interface EvaluationCardCNProps {
	name: string
	description?: string
	marketArea?: string
	status: string
	updatedAt?: string
	linkUrl: string
	siteLocations: EvaluationCardCNSite[]
	siteCount?: number
}

/**
 * Card form mirrors LocationCardCN/ProjectCardCN/PortfolioCardCN:
 * h-[132px] horizontal, 106px image area (left) + content stack (right).
 *
 * Per spec (MYD-258), the status pill lives on the content top-row right
 * (rather than the bottom-row action position other cards use) so the
 * evaluation's status reads at a glance.
 */
export const EvaluationCardCN = ({
	name,
	description,
	marketArea,
	status,
	updatedAt,
	linkUrl,
	siteLocations,
	siteCount,
}: EvaluationCardCNProps) => {
	const images = siteLocations
		.map(s => s.imageUrl)
		.filter((url): url is string => Boolean(url))
		.slice(0, 4)
	const imageCount = images.length

	const formattedDate = updatedAt
		? new Date(updatedAt).toLocaleDateString('en-US', {
				month: '2-digit',
				day: '2-digit',
				year: 'numeric',
			})
		: null

	const statusLabel = status?.replace(/_/g, ' ') || ''
	const statusClass = STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'

	return (
		<Link to={linkUrl} className="no-underline text-inherit hover:no-underline">
			<div className="bg-white rounded-2xl border border-gray-200 shadow-md overflow-hidden h-[132px] flex hover:shadow-lg hover:border-gray-300 hover:scale-105 transition-all duration-200 group">
				{/* Image area — square multi-image mosaic of up to 4 site location images */}
				<div className="w-[106px] h-full flex-shrink-0 relative overflow-hidden">
					{imageCount === 0 ? (
						<div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
							<ClipboardCheck className="w-8 h-8 text-[#0080fe]" />
						</div>
					) : imageCount === 1 ? (
						<div
							className="w-full h-full bg-cover bg-center group-hover:scale-105 transition-transform duration-300"
							style={{ backgroundImage: `url(${images[0]})` }}
						/>
					) : imageCount === 2 ? (
						<div className="w-full h-full grid grid-cols-2 gap-[1px]">
							{images.map((img, i) => (
								<div
									key={i}
									className="bg-cover bg-center"
									style={{ backgroundImage: `url(${img})` }}
								/>
							))}
						</div>
					) : imageCount === 3 ? (
						<div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-[1px]">
							<div
								className="bg-cover bg-center row-span-2"
								style={{ backgroundImage: `url(${images[0]})` }}
							/>
							<div
								className="bg-cover bg-center"
								style={{ backgroundImage: `url(${images[1]})` }}
							/>
							<div
								className="bg-cover bg-center"
								style={{ backgroundImage: `url(${images[2]})` }}
							/>
						</div>
					) : (
						<div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-[1px]">
							{images.map((img, i) => (
								<div
									key={i}
									className="bg-cover bg-center"
									style={{ backgroundImage: `url(${img})` }}
								/>
							))}
						</div>
					)}
					{/* Type icon overlay when at least one image is shown */}
					{imageCount > 0 && (
						<div className="absolute top-1.5 left-1.5 bg-white shadow-sm rounded-full p-1">
							<ClipboardCheck className="w-3 h-3 text-[#0080fe]" />
						</div>
					)}
				</div>

				{/* Content area */}
				<div className="flex-1 min-w-0 flex flex-col justify-between p-3">
					{/* Top row: name + status pill */}
					<div className="flex items-start gap-2 min-w-0">
						<h3 className="flex-1 text-sm font-semibold text-gray-900 truncate leading-tight m-0 min-w-0">
							{name}
						</h3>
						{statusLabel && (
							<Badge className={cn('flex-shrink-0 text-[10px] px-1.5 py-0 h-4', statusClass)}>
								{statusLabel}
							</Badge>
						)}
					</div>

					{/* Body: market area + description */}
					<div className="min-w-0 mt-0.5">
						{marketArea && (
							<p className="text-xs text-gray-500 truncate flex items-center gap-1 leading-tight m-0">
								<MapPin className="w-3 h-3 flex-shrink-0" />
								{marketArea}
							</p>
						)}
						{description && (
							<p className="text-xs text-gray-400 truncate mt-0.5 mb-0 leading-tight">
								{description}
							</p>
						)}
					</div>

					{/* Bottom row: site count badge + updated date */}
					<div className="flex items-center gap-1.5 mt-auto">
						{typeof siteCount === 'number' && siteCount > 0 && (
							<Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 gap-0.5">
								<Building2 className="w-2.5 h-2.5" />
								{siteCount}
							</Badge>
						)}
						{formattedDate && (
							<span className="text-[10px] text-gray-400">Updated {formattedDate}</span>
						)}
					</div>
				</div>
			</div>
		</Link>
	)
}

export default EvaluationCardCN
